import { TextureColorSpace, TextureFormat } from '@seddi/surface-material';
import {
  type LoadingManager,
  type WebGLRenderer,
  RepeatWrapping,
  SRGBColorSpace,
  Texture,
  UVMapping,
} from '@seddi/three';
import { EXRLoader } from '@seddi/three/examples/jsm/loaders/EXRLoader.js';
import log from 'loglevel';

import { ImageBitmapLoader } from '../loaders/imageBitmapLoader';
import { premultiplyAlphaSRGB } from './texturePremultipliedAlpha';
import type { TextureDesc } from './textureTypes';

interface Options {
  premultiplyAlpha?: 'none' | 'premultiply';
}

/**
 * Load texture.
 * @param desc Texture desc.
 * @param manager Loading manager.
 * @param options Texture options.
 */
export const load = async (
  desc: TextureDesc,
  manager: LoadingManager,
  renderer: WebGLRenderer,
  options?: Options,
): Promise<Texture | undefined> => {
  let texture!: Texture;

  // Prioritize image bitmap if available.
  if (desc.imageBitmap) {
    texture = new Texture(desc.imageBitmap, UVMapping);
    texture.needsUpdate = true;
  } else {
    switch (desc.format) {
      case TextureFormat.EXR: {
        const loader = new EXRLoader(manager);
        texture = await loader.loadAsync(desc.href as string);

        if (!texture) {
          log.error(`Error loading texture ${desc.href}`);
          return undefined;
        }

        texture.wrapS = RepeatWrapping;
        texture.wrapT = RepeatWrapping;

        break;
      }
      case TextureFormat.JPEG:
      case TextureFormat.PNG:
      case TextureFormat.WEBP: {
        const loader = new ImageBitmapLoader(manager);
        loader.setOptions({ imageOrientation: 'flipY', premultiplyAlpha: 'none' });
        const image = await loader.loadAsync(desc.href as string);
        if (!image) {
          log.error(`Error loading texture ${desc.href}`);
          return undefined;
        }
        texture = new Texture(image, UVMapping);
        texture.needsUpdate = true;

        break;
      }
      default: {
        log.error(`Unrecognized texture format ${desc.format}`);
        return undefined;
      }
    }
  }

  if (desc.colorSpace === TextureColorSpace.GAMMA) {
    texture.colorSpace = SRGBColorSpace;

    // Handle premultiplied alpha with shader when color space is sRGB
    if (options?.premultiplyAlpha === 'premultiply') {
      const premultipliedTexture = premultiplyAlphaSRGB(renderer, texture);
      texture.dispose();
      texture = premultipliedTexture;
    }
  }

  return texture;
};
